/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TwoFactorMethod } from '../../types/twoFactor';
import { sendTwoFactorCode } from '../../pages/user/login/services/post/login/twoFactorAuth';
import EmailIcon from '@mui/icons-material/Email';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import {
  TwoFactorCodeInput,
  dialogPaperSx,
  buttonSx,
} from '../TwoFactor';

/** Info alert style */
const infoAlertSx = {
  mb: 3,
  textAlign: 'left',
  backgroundColor: 'rgba(76, 175, 80, 0.1)',
  color: '#4caf50',
  borderRadius: 2,
  '& .MuiAlert-icon': { color: '#4caf50' },
};

/** Error alert style */
const errorAlertSx = {
  mb: 3,
  textAlign: 'left',
  backgroundColor: 'rgba(244, 67, 54, 0.1)',
  color: '#f44336',
  borderRadius: 2,
  '& .MuiAlert-icon': { color: '#f44336' },
};

/** Resend button style */
const resendButtonSx = {
  textTransform: 'none',
  color: '#354EEF',
  fontWeight: 600,
  '&:hover': { background: 'rgba(53, 78, 239, 0.1)' },
};

interface TwoFactorAuthModalProps {
  open: boolean;
  email: string;
  availableMethods: TwoFactorMethod[];
  onVerify: (method: TwoFactorMethod, code: string) => Promise<void>;
  onClose?: () => void;
  title?: string;
}

/**
 * Login-time 2FA verification modal.
 * Supports single-method and sequential dual-method (EMAIL → APP) verification flows.
 * In dual-method mode, the user first verifies EMAIL (step 1/2) then APP (step 2/2).
 * Delegates actual verification to the parent via the `onVerify` callback.
 */
export const TwoFactorAuthModal: React.FC<TwoFactorAuthModalProps> = ({
  open,
  email,
  availableMethods,
  onVerify,
  onClose,
  title,
}) => {
  const { t } = useTranslation('login');
  const requireBothMethods = availableMethods.length > 1;

  // Sequential dual-method state: current step (1 = EMAIL, 2 = APP)
  const [dualStep, setDualStep] = useState<1 | 2>(1);

  // Single-method state
  const [selectedMethod, setSelectedMethod] = useState<TwoFactorMethod>(() =>
    availableMethods.length > 0 ? availableMethods[0] : TwoFactorMethod.EMAIL
  );
  const [code, setCode] = useState('');

  // Shared state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [info, setInfo] = useState<string | null>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setCode('');
      setDualStep(1);
      setError(null);
      setInfo(null);
      setIsSubmitting(false);
      setResendCooldown(0);
    }
  }, [open]);

  // Update selected method for single-method mode
  useEffect(() => {
    if (availableMethods.length === 1) {
      setSelectedMethod(availableMethods[0]);
    }
  }, [availableMethods]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const intervalId = window.setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, [resendCooldown]);

  /** Extracts a user-friendly error message from a caught error. */
  const extractErrorMessage = (err: any, fallbackKey: string, fallbackText: string): string => {
    if (err?.isRateLimited || err?.response?.status === 429) {
      return t('ERROR_RATE_LIMITED') || 'Too many attempts. Please try again later.';
    }
    return (
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      t(fallbackKey) ||
      fallbackText
    );
  };

/** Handles verification for the current step in dual-method sequential flow. */
  const handleVerifyDualStep = async () => {
    setError(null);
    setInfo(null);

    if (code.trim().length !== 6) {
      setError(t('ERROR_EMPTY_2FA_CODE') || 'Please enter the verification code.');
      return;
    }

    setIsSubmitting(true);
    const currentMethod = dualStep === 1 ? TwoFactorMethod.EMAIL : TwoFactorMethod.APP;

    try {
      // On exécute la vérification
      await onVerify(currentMethod, code.trim());

      // Si on arrive ici, c'est que la promesse s'est résolue avec succès (pas d'erreur levée).
      
      // LOGIQUE DE TRANSITION ETAPE 1 -> ETAPE 2
      if (dualStep === 1) {
        // On force le nettoyage du state pour préparer l'étape suivante
        setCode(''); 
        setError(null);
        
        // Message de succès pour l'utilisateur
        setInfo(t('INFO_STEP_1_SUCCESS') || 'Email verified. Please check your authenticator app.');
        
        // CHANGEMENT D'ETAPE CRITIQUE
        // On utilise setDualStep dans une callback pour s'assurer qu'on a la dernière valeur
        setDualStep((prevStep) => 2); 
      }
      
      // Si on est à l'étape 2, le AuthContext aura mis à jour l'utilisateur et déclenché la redirection
      
    } catch (err: any) {
      console.error("2FA Verification Error:", err); // Debug log

      // Gestion des erreurs
      if (err?.isRateLimited || err?.response?.status === 429) {
        setError(extractErrorMessage(err, 'ERROR_RATE_LIMITED', 'Too many attempts. Please try again later.'));
      } else {
        // Si le code est invalide ou expiré
        setError(extractErrorMessage(err, 'ERROR_INVALID_CODE', 'Invalid code. Please try again.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySingleMethod = async () => {
    setError(null);

    if (!code.trim()) {
      setError(t('ERROR_EMPTY_2FA_CODE') || 'Please enter the 2FA code.');
      return;
    }

    setIsSubmitting(true);

    try {
      await onVerify(selectedMethod, code.trim());
      setCode('');
    } catch (err: any) {
      if (err?.isRateLimited || err?.response?.status === 429) {
        setError(t('ERROR_RATE_LIMITED') || 'Too many attempts. Please try again later.');
        setIsSubmitting(false);
        return;
      }

      if (err?.partialSuccess && err?.validatedMethod) {
        setError(t('ERROR_MULTIPLE_METHODS_REQUIRED') || 'Multiple authentication methods are required. Please contact support.');
        setIsSubmitting(false);
        return;
      }

      setError(extractErrorMessage(err, 'ERROR_2FA_GENERIC', 'Two factor authentication failed.'));
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || isSendingCode) return;

    setError(null);
    setInfo(null);
    setIsSendingCode(true);

    try {
      await sendTwoFactorCode(email);
      setResendCooldown(60);
      setInfo(t('INFO_2FA_CODE_RESENT') || 'A new authentication code has been sent to your email.');
    } catch (err: any) {
      setError(extractErrorMessage(err, 'ERROR_2FA_RESEND_GENERIC', 'Failed to resend the authentication code.'));
    } finally {
      setIsSendingCode(false);
    }
  };

  /** Renders the resend code button with cooldown */
  const renderResendButton = () => (
    <Button
      variant="text"
      size="small"
      onClick={handleResendCode}
      disabled={isSendingCode || resendCooldown > 0}
      sx={resendButtonSx}
    >
      {isSendingCode ? (
        <CircularProgress size={16} sx={{ color: '#354EEF' }} />
      ) : resendCooldown > 0 ? (
        `${resendCooldown}s`
      ) : (
        t('BUTTON_RESEND_CODE') || 'Resend'
      )}
    </Button>
  );

  /** Renders info and error alerts */
  const renderAlerts = () => (
    <>
      {info && <Alert severity="success" sx={infoAlertSx}>{info}</Alert>}
      {error && <Alert severity="error" sx={errorAlertSx}>{error}</Alert>}
    </>
  );

  /** Renders the cancel button */
  const renderCancelButton = () =>
    onClose ? (
      <Button
        variant="text"
        fullWidth
        onClick={onClose}
        disabled={isSubmitting}
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          textTransform: 'none',
          '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
        }}
      >
        {t('BUTTON_CANCEL') || 'Cancel'}
      </Button>
    ) : null;

  // ─── Dual-method sequential mode (EMAIL step 1/2 → APP step 2/2) ─────────
  if (requireBothMethods) {
    const isEmailStep = dualStep === 1;
    const stepLabel = `${dualStep}/2`;

    const getStepIcon = () =>
      isEmailStep ? (
        <EmailIcon sx={{ color: '#354EEF', fontSize: 48, mb: 2 }} />
      ) : (
        <PhoneAndroidIcon sx={{ color: '#354EEF', fontSize: 48, mb: 2 }} />
      );

    const getStepTitle = () =>
      isEmailStep
        ? title || t('TITLE_2FA_EMAIL_STEP') || 'Email Verification'
        : title || t('TITLE_2FA_APP_STEP') || 'Authenticator Verification';

    const getStepInstruction = () =>
      isEmailStep
        ? t('INSTRUCTION_EMAIL_2FA') || `A verification code has been sent to ${email}. Please enter it below.`
        : t('INSTRUCTION_APP_2FA') || 'Open your authenticator app and enter the 6-digit code.';

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isSubmitting}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Step indicator */}
            <Typography
              variant="overline"
              sx={{
                color: '#354EEF',
                fontWeight: 700,
                fontSize: '0.85rem',
                letterSpacing: '0.1em',
                mb: 1,
                display: 'block',
              }}
            >
              {t('STEP_LABEL') || 'Step'} {stepLabel}
            </Typography>

            {getStepIcon()}

            <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: '#FFFFFF' }}>
              {getStepTitle()}
            </Typography>

            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 4 }}>
              {getStepInstruction()}
            </Typography>

            {renderAlerts()}

            {/* Code input form */}
            <Box
              sx={{
                p: 3,
                mb: 3,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <TwoFactorCodeInput
                value={code}
                onChange={setCode}
                disabled={isSubmitting}
                autoFocus
                onEnter={() => code.trim().length === 6 && handleVerifyDualStep()}
                sx={{ mb: 2 }}
              />

              {/* Resend code - only for EMAIL step */}
              {isEmailStep && (
                <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                    {t('TEXT_DID_NOT_RECEIVE_CODE') || "Didn't receive the code?"}{' '}
                  </Typography>
                  {renderResendButton()}
                </Box>
              )}
            </Box>

            {/* Verify button */}
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleVerifyDualStep}
              disabled={isSubmitting || code.trim().length < 6}
              sx={buttonSx}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#354EEF' }} />
                  {t('BUTTON_VERIFYING') || 'Verifying...'}
                </>
              ) : (
                t('BUTTON_VERIFY') || 'Verify'
              )}
            </Button>

            {renderCancelButton()}
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // ─── Single-method mode ───────────────────────────────────────────────────
  const getInstructionText = () => {
    if (selectedMethod === TwoFactorMethod.EMAIL) {
      return t('INSTRUCTION_EMAIL_2FA') || `A verification code has been sent to ${email}. Please enter it below.`;
    }
    if (selectedMethod === TwoFactorMethod.APP) {
      return t('INSTRUCTION_APP_2FA') || 'Open your authenticator app and enter the 6-digit code.';
    }
    return '';
  };

  const getIcon = () => {
    if (selectedMethod === TwoFactorMethod.EMAIL) {
      return <EmailIcon sx={{ color: '#354EEF', fontSize: 48, mb: 2 }} />;
    }
    if (selectedMethod === TwoFactorMethod.APP) {
      return <PhoneAndroidIcon sx={{ color: '#354EEF', fontSize: 48, mb: 2 }} />;
    }
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          {getIcon()}

          <Typography variant="h4" sx={{ mb: 2, fontWeight: 700, color: '#FFFFFF' }}>
            {title || t('TITLE_2FA') || 'Two-Factor Authentication'}
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 4 }}>
            {getInstructionText()}
          </Typography>

          {renderAlerts()}

          {/* Code input form */}
          <Box
            sx={{
              p: 3,
              mb: 3,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <TwoFactorCodeInput
              value={code}
              onChange={setCode}
              disabled={isSubmitting}
              autoFocus
              onEnter={() => code.trim() && handleVerifySingleMethod()}
              sx={{ mb: 2 }}
            />

            {/* Resend code - only for EMAIL method */}
            {selectedMethod === TwoFactorMethod.EMAIL && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)' }}>
                  {t('TEXT_DID_NOT_RECEIVE_CODE') || "Didn't receive the code?"}{' '}
                </Typography>
                {renderResendButton()}
              </Box>
            )}
          </Box>

          {/* Verify button */}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleVerifySingleMethod}
            disabled={isSubmitting || !code.trim() || code.length < 6}
            sx={buttonSx}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: '#354EEF' }} />
                {t('BUTTON_VERIFYING') || 'Verifying...'}
              </>
            ) : (
              t('BUTTON_VERIFY') || 'Verify'
            )}
          </Button>

          {renderCancelButton()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default TwoFactorAuthModal;
